import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Col, ErrorSummary, FormSelect, H3, Row, SectionPage } from '@snsw/react-component-library';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import ScrollToTop from 'shared/utils/scrollToTop';
import { PressureVessel } from './plantItem/PressureVessel';
import styles from './PlantItemDetails.module.scss';
import {
  addPlantItem,
  resetAssociatedDesign,
  resetRelatedDetails,
  storePlantDetails,
} from 'plantItemNew/redux/actions';
import { AmusementDevice } from './plantItem/AmusementDevice';
import { Boiler } from './plantItem/Boiler';
import { BuildingMaintenanceUnit } from './plantItem/BuildingMaintenanceUnit';
import { ConcretPlacingBoom } from './plantItem/ConcretPlacingBoom';
import { Escalator } from './plantItem/Escalator';
import { Lift } from './plantItem/Lift';
import { MobileCrane } from './plantItem/MobileCrane';
import { MovingWalkway } from './plantItem/MovingWalkway';
import { TowerCrane } from './plantItem/TowerCrane';
import { RefData } from 'plantItemNew/constants/refData';
import { PagePath } from 'shared/constants/pagePath';
import { saveApplication } from 'shared/redux/applicationSlice';
import { SaveOrCancelItem } from './SaveOrCancelItem';

export const PlantItemDetails = (props) => {
  ScrollToTop();

  const { index } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const savedPlantItems = useSelector((state) => state.plantItems);
  const [itemDetails, setItemDetails] = useState();
  const [currentItemObject, setCurrentItemObject] = useState();
  const [itemType, setItemType] = useState('');

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({ itemType: false });
  const [parentErrorsSummary, setParentErrorSummary] = useState([]);
  const [childErrorSummary, setChildErrorSummary] = useState([]);
  const [combinedErrorSummary, setCombinedErrorSummary] = useState([]);

  const refData = useSelector((state) => state.referenceData.data);

  useEffect(() => {
    if (Number.isInteger(+index) && savedPlantItems.items[index]) {
      let itemObject = savedPlantItems.items[index];
      setCurrentItemObject(itemObject);
      setItemType(itemObject?.itemDetails?.itemType);
      setItemDetails(itemObject?.itemDetails);
    }
  }, [savedPlantItems, index]);

  useEffect(() => {
    if (showErrors) validateExt(validators, setErrors, setParentErrorSummary);
  }, [itemType]);

  useEffect(() => {
    setCombinedErrorSummary(parentErrorsSummary.concat(childErrorSummary));
  }, [parentErrorsSummary, childErrorSummary]);

  const plantItemTypeOptions =
    refData && Array.isArray(refData['plant_itemtype'])
      ? refData['plant_itemtype'].map((e) => ({
          value: e.tangibletypecode,
          text: e.description,
        }))
      : [];

  const validators = [
    {
      fieldName: 'itemType',
      fieldValue: itemType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
  ];

  const itemRef = React.useRef();

  const getItemObject = () => {
    let currentComponentValid = validateExt(validators, setErrors, setParentErrorSummary);
    let isItemDetailsValid = itemRef?.current?.validate();

    let itemTypeText = '';
    if (Array.isArray(refData?.plant_itemtype)) {
      let plantItem = refData?.plant_itemtype.find((x) => x.tangibletypecode === itemType);
      if (plantItem) itemTypeText = plantItem.description;
    }

    let plantDetails = {
      isCompleted: currentComponentValid && isItemDetailsValid,
      itemType,
      itemTypeText,
      ...itemDetails,
    };

    return plantDetails;
  };

  const savePlantItemDetails = (plantDetails, index) => {
    let plantItemTypeChanged = false;
    if (plantDetails.itemType !== currentItemObject?.itemDetails?.itemType) plantItemTypeChanged = true;

    //if user modify itemtype, delete other details data
    if (plantItemTypeChanged && index !== 'new') {
      dispatch(resetAssociatedDesign(index));
      dispatch(resetRelatedDetails(index));
    }

    let newIndex = 0;

    if (index === 'new') {
      newIndex = savedPlantItems.items.length;
      dispatch(addPlantItem(plantDetails));
      navigate(`/plant-details/${newIndex}`, { replace: true });
    } else {
      newIndex = index;
      dispatch(storePlantDetails(plantDetails, index));
    }

    dispatch(saveApplication(true));

    return newIndex;
  };

  const onSaveClick = () => {
    let currentComponentValid = validateExt(validators, setErrors, setParentErrorSummary);
    if (!currentComponentValid) {
      setShowErrors(true);
    } else {
      let plantDetails = getItemObject();
      savePlantItemDetails(plantDetails, index);
      navigate(PagePath.plantItemList);
    }
  };

  const onContinueClick = () => {
    let plantDetails = getItemObject();
    if (plantDetails.isCompleted) {
      let itemIndex = savePlantItemDetails(plantDetails, index);
      navigate(`${PagePath.associatedDesign}/${itemIndex}`);
    } else {
      setShowErrors(true);
    }
  };

  const renderDetails = (itemType) => {
    switch (itemType) {
      case RefData.plantItemType.boiler:
        return (
          <Boiler
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      case RefData.plantItemType.pressureVessel:
        return (
          <PressureVessel
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      case RefData.plantItemType.lift:
        return (
          <Lift
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      case RefData.plantItemType.escalator:
        return (
          <Escalator
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      case RefData.plantItemType.movingWalkway:
        return (
          <MovingWalkway
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      case RefData.plantItemType.amusementDevice:
        return (
          <AmusementDevice
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      case RefData.plantItemType.mobileCrane:
        return (
          <MobileCrane
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      case RefData.plantItemType.towerCrane:
        return (
          <TowerCrane
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      case RefData.plantItemType.buildingMaintenanceUnit:
        return (
          <BuildingMaintenanceUnit
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      case RefData.plantItemType.concretePlacingBoom:
        return (
          <ConcretPlacingBoom
            ref={itemRef}
            onChange={setItemDetails}
            value={itemDetails}
            refData={refData}
            onErrorChange={setChildErrorSummary}
          />
        );

      default:
        return null;
    }
  };

  return index === 'new' || currentItemObject ? (
    <>
      {showErrors && <ErrorSummary errors={combinedErrorSummary} />}
      <SectionPage>
        <H3>Plant item details</H3>
        <Row>
          <Col span={8}>
            <FormSelect
              errorMessage={errors?.itemType?.errorMessage}
              id="itemType"
              label="Type of plant item"
              name="itemType"
              options={plantItemTypeOptions}
              placeholder="Please select"
              onChange={(e) => {
                setItemType(e.target.value);
              }}
              value={itemType}
              hasError={showErrors && errors?.itemType?.invalid}
              inputWidth="lg"
            />
          </Col>
        </Row>
        {renderDetails(itemType)}
      </SectionPage>

      <SectionPage className={styles.marginTopThreeHalfRem}>
        <H3>Associated design details</H3>
        <Button className={styles.marginTopOneHalf} onClick={onContinueClick}>
          Next
        </Button>
      </SectionPage>

      <SaveOrCancelItem onSaveClick={onSaveClick} index={index} />
    </>
  ) : null;
};
